<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in filteredRoutes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children || []" :key="index2">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(route.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { useAccessControl } from '../../composables/useAccessControl'
import { useAuthStore } from '../../stores/auth'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const authStore = useAuthStore()
    const route = useRoute()
    const { t } = useI18n()

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      // Special case for projects section
      if (section.name === 'projects') {
        return isProjectsSectionActive()
      }

      if (!section.children) {
        return route.name === section.name
      }

      // Check if current route is a child of this section
      return section.children.some((child) => route.name === child.name)
    }

    // Special logic for projects section to highlight when on project-detail or form routes
    const isProjectsSectionActive = () => {
      const currentRouteName = route.name as string
      return (
        currentRouteName === 'projects' ||
        currentRouteName === 'project-detail' ||
        currentRouteName?.startsWith('form-')
      )
    }

    const setActiveExpand = () => {
      const newValue = navigationRoutes.routes.map((route: INavigationRoute) => routeHasActiveChild(route))
      value.value = newValue
    }

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    const isClient = () => Boolean(authStore.user?.is_client)

    const filterRoutesForClient = (routes: INavigationRoute[]): INavigationRoute[] => {
      if (!isClient()) return routes
      // remove routes with disallowClients
      const filtered = routes
        .filter((r) => !r.meta?.disallowClients)
        .map((r) => {
          if (!r.children) return r
          const children = r.children.filter((c) => !(c as any).meta?.disallowClients)
          return { ...r, children }
        })
      return filtered
    }

    // Apply permission-based filtering (deny-by-default) using access control
    const filteredRoutes = ref<INavigationRoute[]>([])
    const { filterRoutesForMenu } = useAccessControl()

    const refreshMenu = async () => {
      // console.log(`[AppSidebar] Refreshing menu for user:`, authStore.user?.email)
      const clientFiltered = filterRoutesForClient(navigationRoutes.routes)
      // console.log(`[AppSidebar] Client filtered routes:`, clientFiltered)

      // For sidebar menu filtering, do NOT force a projectId for org-scoped routes.
      // Leave projectId undefined so org-level items (e.g., preferences, faq) can be evaluated correctly
      const projectId = route.params.projectId ? parseInt(route.params.projectId as string) : undefined

      try {
        const byPermissions = await filterRoutesForMenu(clientFiltered as any, projectId)
        // console.log(`[AppSidebar] Permission filtered routes:`, byPermissions)
        filteredRoutes.value = byPermissions as INavigationRoute[]
      } catch (e) {
        console.error(`[AppSidebar] Error filtering routes:`, e)
        filteredRoutes.value = clientFiltered
      }
    }

    // initialize and react to route/auth changes
    setActiveExpand()
    refreshMenu()
    watch(
      () => route.fullPath,
      () => {
        setActiveExpand()
        refreshMenu()
      },
      { immediate: true },
    )
    watch(
      () => authStore.user,
      () => refreshMenu(),
      { deep: true },
    )

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      filteredRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      isProjectsSectionActive,
      t,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>
